import { spokenNumberWordsToDigits } from "./spokenNumberWords.js";

const ITEM_WORD =
  "one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty";

const ITEM_MARKER = new RegExp(
  `\\bitem\\s+(?:(?:number|no\\.?)\\s+|#\\s*)?(?:[1-9]\\d?|${ITEM_WORD})\\b`,
  "i",
);

/** Intentional hand-off to the next condition. "is also" stays one item. */
const LOOSE_MARKER =
  /\b(?:and\s+then|and\s+also|another(?:\s+(?:thing|one))?|also|next)\b/i;

const SAME_CONDITION = new Set([
  "is",
  "are",
  "was",
  "were",
  "be",
  "been",
  "being",
  "looks",
  "seems",
  "still",
]);

const SPOKEN_DIGIT: Record<string, string> = {
  zero: "0",
  oh: "0",
  o: "0",
  one: "1",
  two: "2",
  three: "3",
  four: "4",
  five: "5",
  six: "6",
  seven: "7",
  eight: "8",
  nine: "9",
};

const MODEL_STOP = new Set([
  "a",
  "an",
  "the",
  "is",
  "it",
  "its",
  "this",
  "that",
  "of",
  "by",
  "and",
]);

function previousWord(text: string, index: number): string {
  const before = text.slice(0, index).trimEnd();
  const word = before.match(/([A-Za-z']+)$/);
  return (word?.[1] ?? "").toLowerCase();
}

function sliceMarkedChunks(
  text: string,
  marker: RegExp,
  dropPreamble: boolean,
  skip?: (text: string, index: number) => boolean,
): string[] {
  const starts: number[] = [];
  const global = new RegExp(marker.source, "gi");
  for (const match of text.matchAll(global)) {
    if (match.index == null) continue;
    if (skip?.(text, match.index)) continue;
    starts.push(match.index);
  }
  if (starts.length === 0) return [text];
  const points = dropPreamble ? starts : starts[0] === 0 ? starts : [0, ...starts];
  const lead = new RegExp(`^\\s*(?:${marker.source})`, "i");
  const chunks: string[] = [];
  for (let i = 0; i < points.length; i += 1) {
    const start = points[i] ?? 0;
    const end = points[i + 1] ?? text.length;
    let chunk = text.slice(start, end).replace(lead, "");
    chunk = chunk.replace(/^[\s:,.\-–—]+/, "");
    chunk = chunk.replace(/^(?:is|was)\s+/i, "");
    chunk = chunk.replace(/[\s,;]+$/, "").trim();
    if (chunk) chunks.push(chunk);
  }
  return chunks.length > 0 ? chunks : [text];
}

/** Numbered items win. Otherwise "also" / "another" / "next" starts a new one. */
export function splitItemizedObservations(transcriptRaw: string): string[] {
  const text = transcriptRaw.trim();
  if (!text) return [];
  if (new RegExp(ITEM_MARKER.source, "i").test(text)) {
    return sliceMarkedChunks(text, ITEM_MARKER, true);
  }
  const loose = sliceMarkedChunks(text, LOOSE_MARKER, false, (source, index) => {
    const word = previousWord(source, index);
    if (SAME_CONDITION.has(word)) return true;
    const matched = source.slice(index).match(LOOSE_MARKER)?.[0].toLowerCase() ?? "";
    return matched === "next" && word === "the";
  });
  if (loose.length > 1) return loose;
  return [text];
}

/** "12 by 20" and "twelve by twenty" → "12x20". */
export function normalizeBuildingSizePhrase(text: string): string {
  const digits = spokenNumberWordsToDigits(text);
  return digits.replace(/\b(\d+)\s*(?:by|x|×)\s*(\d+)\b/gi, "$1x$2").replace(/\s+/g, " ").trim();
}

const SPOKEN_DIGIT_WORD =
  "zero|three|seven|eight|four|five|nine|one|two|six|oh|o";

function readSerialAfterLabel(after: string): { serial: string; consumed: number } | null {
  const spoken = after.match(
    new RegExp(`^((?:${SPOKEN_DIGIT_WORD})(?:[\\s-]+(?:${SPOKEN_DIGIT_WORD}))*)`, "i"),
  );
  if (spoken?.[1] && spoken[1].split(/[\s-]+/).length >= 2) {
    const digits = spoken[1]
      .split(/[\s-]+/)
      .map((word) => SPOKEN_DIGIT[word.toLowerCase()] ?? "")
      .join("");
    if (digits) return { serial: digits, consumed: spoken[1].length };
  }
  const compact = after.match(/^([a-z]*\d[a-z0-9-]*)/i);
  if (!compact?.[1]) return null;
  return { serial: compact[1], consumed: compact[1].length };
}

function modelPhraseFromRemainder(rest: string): string {
  const sentences = rest.split(/(?<=[.!?])\s+/);
  const kept: string[] = [];
  for (const sentence of sentences) {
    let s = sentence.replace(/^[\s,.\-]+/, "").trim();
    if (/^(?:i(?:'m| am)|we(?:'re| are))\s+(?:about to\s+)?(?:qc|inspect|check)\b/i.test(s)) continue;
    let prev = "";
    while (s !== prev) {
      prev = s;
      s = s.replace(/^(?:and\s+)?(?:this|it|that)(?:\s+is|'s)\s+(?:an?\s+)?/i, "").trim();
    }
    s = s.replace(/[.!?]+$/, "").trim();
    if (s) kept.push(s);
  }
  return normalizeBuildingSizePhrase(kept.join(" "));
}

/** Serial digits plus the building phrase, without the spoken lead-in. */
export function parseQaSetupSpeech(transcript: string): { serial: string; model: string } {
  const text = transcript.trim().replace(/\s+/g, " ");
  if (!text) return { serial: "", model: "" };

  const label = text.match(/\bserial(?:\s+number)?\s+#?\s*/i);
  if (label && label.index != null) {
    const after = text.slice(label.index + label[0].length);
    const read = readSerialAfterLabel(after);
    if (read) {
      const rest = `${text.slice(0, label.index)} ${after.slice(read.consumed)}`.replace(/\s+/g, " ").trim();
      return { serial: read.serial, model: modelPhraseFromRemainder(rest) };
    }
  }

  const parts = text.split(/\s+/);
  const first = parts[0] ?? "";
  if (/^\d[\d-]*$/.test(first)) {
    return {
      serial: first,
      model: modelPhraseFromRemainder(parts.slice(1).join(" ")),
    };
  }
  return { serial: "", model: modelPhraseFromRemainder(text) };
}

const NAME_STOP = new Set(["and", "the", "then", "who", "after", "with", "on", "for"]);

function readSpokenFixerName(after: string): { name: string; consumed: number } {
  const words = after.trim().split(/\s+/).filter(Boolean);
  const name: string[] = [];
  let consumed = 0;
  const source = after.trimStart();
  let cursor = 0;
  for (const word of words) {
    const at = source.indexOf(word, cursor);
    const clean = word.replace(/[.,!?]+$/g, "");
    if (!/^[A-Za-z][A-Za-z'’.-]*$/.test(clean)) break;
    if (name.length > 0 && NAME_STOP.has(clean.toLowerCase())) break;
    name.push(clean);
    cursor = at + word.length;
    consumed = cursor;
    if (name.length === 3 || /[.,!?]$/.test(word)) break;
  }
  return { name: name.join(" "), consumed };
}

/** Pull "fixed by {name}" out of a fix note. The rest is the description. */
export function parseFixSpeech(
  transcript: string,
  assumedName: string,
): { description: string; fixedByName: string } {
  const text = transcript.trim().replace(/\s+/g, " ");
  const assumed = assumedName.trim();
  const marker = text.match(/\bfixed\s+by\s+/i);
  if (!marker || marker.index == null) {
    return { description: text, fixedByName: assumed };
  }
  const after = text.slice(marker.index + marker[0].length);
  const spoken = readSpokenFixerName(after);
  const description = `${text.slice(0, marker.index)} ${after.slice(spoken.consumed)}`
    .replace(/\s+/g, " ")
    .trim();
  return {
    description,
    fixedByName: spoken.name || assumed,
  };
}

/** Full phrase, or every meaningful word, appears in the model name or code. */
export function modelQueryMatches(queryRaw: string, name: string, code: string): boolean {
  const query = queryRaw.trim().toLowerCase();
  if (!query) return false;
  const hay = `${name} ${code}`.toLowerCase();
  if (hay.includes(query)) return true;
  const tokens = query.split(/[^a-z0-9]+/).filter((token) => token && !MODEL_STOP.has(token));
  if (tokens.length === 0) return false;
  return tokens.every((token) => hay.includes(token));
}
